import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { SERVICES, TESTIMONIALS, STATS, LOGO_URL, PORTFOLIO, PRODUCTS, BLOGS } from '../data';
import { useReveal, useCountUp, useInView } from '../hooks/useReveal';
import Ticker from '../components/Ticker';

/* ── Stat counter card ── */
function StatCard({ value, suffix, label }) {
  const [ref, inView] = useInView(0.3);
  const count = useCountUp(value, 1800, inView);
  return (
    <div ref={ref} style={{ minWidth:90 }}>
      <div className="stat-num">{count}<span style={{ fontSize:'1.15rem' }}>{suffix}</span></div>
      <div className="stat-lbl">{label}</div>
    </div>
  );
}

/* ── Typing animation hook ── */
function useTyping(words, speed = 90, pause = 1800) {
  const [display, setDisplay] = useState('');
  const [wordIdx, setWordIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const current = words[wordIdx];
    let timeout;
    if (!deleting && charIdx < current.length) {
      timeout = setTimeout(() => setCharIdx(i => i + 1), speed);
    } else if (!deleting && charIdx === current.length) {
      timeout = setTimeout(() => setDeleting(true), pause);
    } else if (deleting && charIdx > 0) {
      timeout = setTimeout(() => setCharIdx(i => i - 1), speed / 2);
    } else {
      setDeleting(false);
      setWordIdx(i => (i + 1) % words.length);
    }
    setDisplay(current.slice(0, charIdx));
    return () => clearTimeout(timeout);
  }, [charIdx, deleting, wordIdx, words, speed, pause]);

  return display;
}

/* ── AI Hero animated background ── */
function AIHeroBackground() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let w = canvas.width  = canvas.offsetWidth;
    let h = canvas.height = canvas.offsetHeight;

    /* Nodes */
    const NODES = 55;
    const nodes = Array.from({ length: NODES }, () => ({
      x: Math.random() * w, y: Math.random() * h,
      vx: (Math.random() - .5) * .55, vy: (Math.random() - .5) * .55,
      r: Math.random() * 2.2 + 1.2,
      hue: Math.random() < .5 ? 340 : (Math.random() < .5 ? 270 : 210),
    }));

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      /* Moving gradient background */
      const t = Date.now() / 6000;
      const grd = ctx.createRadialGradient(
        w * (.5 + .2 * Math.sin(t)), h * (.5 + .15 * Math.cos(t)), 0,
        w * .5, h * .5, Math.max(w, h)
      );
      grd.addColorStop(0,   'rgba(38,14,60,.97)');
      grd.addColorStop(.45, 'rgba(24,9,46,.98)');
      grd.addColorStop(1,   'rgba(8,5,22,1)');
      ctx.fillStyle = grd;
      ctx.fillRect(0, 0, w, h);

      /* Connections */
      const DIST = 160;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const d  = Math.sqrt(dx * dx + dy * dy);
          if (d < DIST) {
            const alpha = (1 - d / DIST) * .35;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(180,120,255,${alpha})`;
            ctx.lineWidth   = 1;
            ctx.stroke();
          }
        }
      }

      /* Nodes */
      nodes.forEach(n => {
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        const ng = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, n.r * 3);
        ng.addColorStop(0, `hsla(${n.hue},90%,72%,.95)`);
        ng.addColorStop(1, `hsla(${n.hue},90%,60%,0)`);
        ctx.fillStyle = ng;
        ctx.fill();
        /* move */
        n.x += n.vx; n.y += n.vy;
        if (n.x < 0 || n.x > w) n.vx *= -1;
        if (n.y < 0 || n.y > h) n.vy *= -1;
      });

      animId = requestAnimationFrame(draw);
    };
    draw();

    const onResize = () => {
      w = canvas.width  = canvas.offsetWidth;
      h = canvas.height = canvas.offsetHeight;
    };
    window.addEventListener('resize', onResize);
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', onResize); };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{ position:'absolute', inset:0, width:'100%', height:'100%', display:'block' }}
    />
  );
}

/* ── Parallax helper for CTA ── */
function ParallaxBg({ url, children, minHeight = '420px', overlay }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onScroll = () => {
      const rect = el.getBoundingClientRect();
      el.style.backgroundPositionY = `calc(50% + ${rect.top * 0.38}px)`;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <div ref={ref} style={{
      minHeight, backgroundImage:`url('${url}')`, backgroundSize:'cover',
      backgroundPosition:'center', backgroundAttachment:'fixed',
      position:'relative', display:'flex', alignItems:'center',
    }}>
      <div style={{ position:'absolute', inset:0, background: overlay || 'linear-gradient(135deg,rgba(232,49,91,.88) 0%,rgba(123,82,171,.82) 50%,rgba(75,184,232,.78) 100%)' }}/>
      <div style={{ position:'relative', zIndex:1, width:'100%' }}>{children}</div>
    </div>
  );
}

/* ── CLIENT LOGOS SCROLLING STRIP ── */
const CLIENT_LOGOS = [
  { name:'TechNova India',   abbr:'TN',  color:'#E8315B' },
  { name:'Growify',          abbr:'GR',  color:'#7B52AB' },
  { name:'SwiftCart',        abbr:'SC',  color:'#4BB8E8' },
  { name:'Nexus Solutions',  abbr:'NS',  color:'#f59e0b' },
  { name:'BrandVibe',        abbr:'BV',  color:'#10b981' },
  { name:'UrbanRetail',      abbr:'UR',  color:'#6366f1' },
  { name:'Medicore Health',  abbr:'MH',  color:'#ec4899' },
  { name:'EduSpark',         abbr:'ES',  color:'#f97316' },
  { name:'Finova Capital',   abbr:'FC',  color:'#14b8a6' },
  { name:'LogiTrack',        abbr:'LT',  color:'#8b5cf6' },
  { name:'AgriSense',        abbr:'AG',  color:'#22c55e' },
  { name:'CloudPeak',        abbr:'CP',  color:'#3b82f6' },
];

function ClientLogosStrip() {
  // duplicate for seamless loop
  const logos = [...CLIENT_LOGOS, ...CLIENT_LOGOS];
  return (
    <section style={{ background:'#fff', padding:'60px 0', overflow:'hidden', borderTop:'1px solid rgba(123,82,171,.08)', borderBottom:'1px solid rgba(123,82,171,.08)' }}>
      <div className="container" style={{ marginBottom:32, textAlign:'center' }}>
        <div className="eyebrow" style={{ justifyContent:'center' }}>Trusted By</div>
        <h2 style={{ fontFamily:"'Outfit',sans-serif", fontSize:'clamp(1.4rem,2.5vw,1.9rem)', fontWeight:900, color:'var(--text)', letterSpacing:'-.035em', lineHeight:1.15 }}>
          150+ Businesses Trust <span className="gt">ITClave</span>
        </h2>
      </div>
      <div style={{ overflow:'hidden', position:'relative' }}>
        {/* Fade edges */}
        <div style={{ position:'absolute', left:0, top:0, bottom:0, width:80, background:'linear-gradient(to right,#fff,transparent)', zIndex:2, pointerEvents:'none' }}/>
        <div style={{ position:'absolute', right:0, top:0, bottom:0, width:80, background:'linear-gradient(to left,#fff,transparent)', zIndex:2, pointerEvents:'none' }}/>
        <div className="logo-scroll-track">
          {logos.map((cl, i) => (
            <div
              key={i}
              style={{
                display:'flex', alignItems:'center', gap:12, flexShrink:0,
                background:'#fff', border:'1.5px solid rgba(123,82,171,.12)',
                borderRadius:14, padding:'14px 24px',
                boxShadow:'0 2px 12px rgba(123,82,171,.07)',
                transition:'all .3s', whiteSpace:'nowrap',
              }}
              onMouseOver={e => { e.currentTarget.style.borderColor = cl.color; e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = `0 6px 24px ${cl.color}22`; }}
              onMouseOut={e  => { e.currentTarget.style.borderColor = 'rgba(123,82,171,.12)'; e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 2px 12px rgba(123,82,171,.07)'; }}
            >
              {/* Logo placeholder — replace src with real logo when available */}
              <div style={{
                width:36, height:36, borderRadius:10,
                background:`linear-gradient(135deg,${cl.color}22,${cl.color}44)`,
                border:`1.5px solid ${cl.color}55`,
                display:'flex', alignItems:'center', justifyContent:'center',
                fontFamily:"'Outfit',sans-serif", fontWeight:900, fontSize:'.72rem', color:cl.color,
                flexShrink:0,
              }}>{cl.abbr}</div>
              <span style={{ fontFamily:"'Outfit',sans-serif", fontWeight:700, fontSize:'.88rem', color:'var(--text)' }}>{cl.name}</span>
            </div>   
          ))}
        </div>
      </div>
      <style>{`
        .logo-scroll-track {
          display: flex;
          gap: 18px;
          width: max-content;
          animation: logoScroll 32s linear infinite;
          padding: 8px 0;
        }
        .logo-scroll-track:hover { animation-play-state: paused; }
        @keyframes logoScroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>
  );
}

/* ════════════════════════════════════════════════
   HOME PAGE
   ════════════════════════════════════════════════ */
export default function Home() {
  useReveal();

  const typedWords = ['Web Applications', 'Mobile Apps', 'ERP Systems', 'E-Commerce Platforms', 'Digital Marketing', 'Custom Software'];
  const typed = useTyping(typedWords);

  return (
    <>
      <Helmet>
        <title>ITClave | Best Software Development & Digital Marketing Company in India</title>
        <meta name="description" content="ITClave is India's most trusted software development and digital marketing company. Custom web apps, mobile apps, SEO, and ERP solutions. 200+ projects. Free consultation!"/>
        <meta name="keywords" content="software development company India, digital marketing agency India, best IT company India, web development India, mobile app development India, SEO services India"/>
        <link rel="canonical" href="https://www.itclave.com/"/>
        <meta property="og:title" content="ITClave | Best IT Company in India"/>
        <meta property="og:description" content="India's trusted software development & digital marketing company. 200+ projects. 4.9★ rated."/>
      </Helmet>

      {/* ══ AI ANIMATED HERO ══════════════════════════════ */}
      <section id="hero" style={{ position:'relative', minHeight:'100vh', display:'flex', alignItems:'center', overflow:'hidden', background:'#0f0720' }}>
        {/* Animated neural network canvas */}
        <AIHeroBackground />

        {/* Subtle grid overlay */}
        <div style={{ position:'absolute', inset:0, zIndex:1, backgroundImage:'linear-gradient(rgba(255,255,255,.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.025) 1px,transparent 1px)', backgroundSize:'60px 60px', pointerEvents:'none' }}/>

        {/* Content */}
        <div style={{ position:'relative', zIndex:2, width:'100%', padding:'120px 0 90px' }}>
          <div className="container">
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:72, alignItems:'center' }} className="hero-2col">

              {/* ── LEFT ── */}
              <div>
                <div style={{ display:'inline-flex', alignItems:'center', gap:9, background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,255,255,.14)', backdropFilter:'blur(12px)', borderRadius:100, padding:'7px 18px', fontSize:'.75rem', fontWeight:700, color:'rgba(255,255,255,.85)', letterSpacing:'.07em', textTransform:'uppercase', marginBottom:26, animation:'fup .6s ease both' }}>
                  <span className="pulse-dot-light"/>
                  AI-Powered · India's #1 IT Partner
                </div>

                <h1 style={{ fontFamily:"'Outfit',sans-serif", fontSize:'clamp(2.4rem,4.5vw,4rem)', fontWeight:900, lineHeight:1.06, letterSpacing:'-.045em', color:'#fff', marginBottom:0, animation:'fup .6s .1s ease both' }}>
                  We Build
                </h1>
                {/* Typing effect line */}
                <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:'clamp(2rem,3.8vw,3.4rem)', fontWeight:900, lineHeight:1.1, letterSpacing:'-.04em', marginBottom:8, animation:'fup .6s .15s ease both', minHeight:'1.2em' }}>
                  <span style={{ background:'linear-gradient(90deg,#E8315B,#B06ADE,#4BB8E8)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text' }}>
                    {typed}
                  </span>
                  <span style={{ display:'inline-block', width:3, height:'0.85em', background:'#B06ADE', marginLeft:2, verticalAlign:'middle', borderRadius:2, animation:'blink 1.1s step-end infinite' }}/>
                </div>
                <h1 style={{ fontFamily:"'Outfit',sans-serif", fontSize:'clamp(2.4rem,4.5vw,4rem)', fontWeight:900, lineHeight:1.06, letterSpacing:'-.045em', color:'#fff', marginBottom:22, animation:'fup .6s .2s ease both' }}>
                  <span style={{ WebkitTextStroke:'2px rgba(255,255,255,.45)', color:'transparent' }}>For India</span>
                </h1>

                <p style={{ fontSize:'1.05rem', color:'rgba(255,255,255,.62)', lineHeight:1.82, maxWidth:500, marginBottom:36, animation:'fup .6s .3s ease both' }}>
                  India's most trusted <strong style={{ color:'#E8315B', fontWeight:700 }}>software development</strong> and{' '}
                  <strong style={{ color:'#4BB8E8', fontWeight:700 }}>digital marketing company</strong>. From MERN stack apps to data-driven SEO — we help businesses grow digitally.
                </p>

                <div style={{ display:'flex', gap:14, flexWrap:'wrap', marginBottom:44, animation:'fup .6s .4s ease both' }}>
                  <Link to="/contact" className="btn-main" style={{ boxShadow:'0 0 32px rgba(232,49,91,.35)' }}>🚀 Start Your Project</Link>
                  <Link to="/portfolio" style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'11px 24px', borderRadius:100, border:'1.5px solid rgba(255,255,255,.2)', color:'rgba(255,255,255,.85)', fontWeight:700, fontSize:'.88rem', backdropFilter:'blur(8px)', background:'rgba(255,255,255,.06)', transition:'all .3s' }}
                    onMouseOver={e=>{ e.currentTarget.style.background='rgba(255,255,255,.12)'; e.currentTarget.style.borderColor='rgba(255,255,255,.4)'; }}
                    onMouseOut={e=>{  e.currentTarget.style.background='rgba(255,255,255,.06)'; e.currentTarget.style.borderColor='rgba(255,255,255,.2)'; }}>
                    📂 View Our Work
                  </Link>
                </div>

                {/* Stats row */}
                <div style={{ display:'flex', gap:32, flexWrap:'wrap', paddingTop:28, borderTop:'1px solid rgba(255,255,255,.1)', animation:'fup .6s .5s ease both' }}>
                  {STATS.map(s => (
                    <div key={s.label} style={{ minWidth:80 }}>
                      <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:'1.8rem', fontWeight:900, letterSpacing:'-.04em', background:'linear-gradient(90deg,#E8315B,#B06ADE,#4BB8E8)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text', lineHeight:1 }}>
                        {s.value}{s.suffix}
                      </div>
                      <div style={{ fontSize:'.7rem', color:'rgba(255,255,255,.45)', fontWeight:600, textTransform:'uppercase', letterSpacing:'.06em', marginTop:3 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── RIGHT: Floating AI card ── */}
              <div style={{ position:'relative', display:'flex', alignItems:'center', justifyContent:'center', minHeight:480 }} className="hero-right-hide">
                {/* Rings */}
                <div style={{ position:'absolute', width:300, height:300, borderRadius:'50%', border:'1px dashed rgba(176,106,222,.25)', animation:'spin 20s linear infinite' }}/>
                <div style={{ position:'absolute', width:400, height:400, borderRadius:'50%', border:'1px dashed rgba(75,184,232,.15)', animation:'spin 32s linear infinite reverse' }}/>
                <div style={{ position:'absolute', width:200, height:200, borderRadius:'50%', border:'1px dashed rgba(232,49,91,.2)', animation:'spin 14s linear infinite' }}/>

                {/* Central logo card */}
                <div style={{
                  background:'rgba(255,255,255,.05)', backdropFilter:'blur(24px)',
                  border:'1px solid rgba(255,255,255,.12)', borderRadius:28,
                  padding:'36px 32px', textAlign:'center', width:200, position:'relative', zIndex:4,
                  boxShadow:'0 24px 80px rgba(0,0,0,.4), 0 0 0 1px rgba(255,255,255,.05)',
                }}>
                  <img src={LOGO_URL} alt="ITClave" style={{ height:52, width:'auto', margin:'0 auto 16px', display:'block' }}/>
                  <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:'1.9rem', fontWeight:900, background:'linear-gradient(90deg,#E8315B,#B06ADE,#4BB8E8)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text', lineHeight:1 }}>4.9★</div>
                  <div style={{ fontSize:'.72rem', color:'rgba(255,255,255,.5)', fontWeight:600, marginTop:4 }}>Google Rating</div>
                </div>

                {/* Floating badges */}
                {[
                  { label:'Web Development',  icon:'💻', pos:{ top:10, left:-20 }, color:'rgba(232,49,91,.12)'  },
                  { label:'Digital Marketing', icon:'📈', pos:{ top:70, right:-5  }, color:'rgba(75,184,232,.12)' },
                  { label:'Mobile Apps',       icon:'📱', pos:{ bottom:80, left:-30 }, color:'rgba(176,106,222,.12)'},
                  { label:'SEO Services',      icon:'🔍', pos:{ bottom:15, right:-10}, color:'rgba(245,158,11,.12)' },
                  { label:'Custom Software',   icon:'⚙️', pos:{ top:'46%', left:-45, transform:'translateY(-50%)' }, color:'rgba(34,197,94,.12)' },
                ].map((fc, i) => (
                  <div key={i} style={{
                    position:'absolute', background:'rgba(255,255,255,.07)', backdropFilter:'blur(12px)',
                    border:'1px solid rgba(255,255,255,.12)', borderRadius:14, padding:'10px 14px',
                    boxShadow:'0 4px 20px rgba(0,0,0,.2)', display:'flex', alignItems:'center', gap:8,
                    fontSize:'.78rem', fontWeight:700, color:'rgba(255,255,255,.85)', whiteSpace:'nowrap', zIndex:3,
                    animation:`fl ${5+i}s ease-in-out infinite ${i * 0.6}s`, ...fc.pos
                  }}>
                    <span style={{ width:28, height:28, borderRadius:8, background:fc.color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'.88rem' }}>{fc.icon}</span>
                    {fc.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom fade to white */}
        <div style={{ position:'absolute', bottom:0, left:0, right:0, height:80, background:'linear-gradient(to bottom,transparent,#f9f8ff)', zIndex:3 }}/>
      </section>

      <Ticker/>

      {/* ══ CLIENT LOGOS SCROLLING STRIP ══════════════════ */}
      <ClientLogosStrip />

      {/* ══ SERVICES PREVIEW (3 cards) ═════════════════════ */}
      <section style={{ background:'#fff', padding:'96px 0' }}>
        <div className="container">
          <div className="sec-hdr">
            <div>
              <div className="eyebrow">What We Do</div>
              <h2 className="sec-title">Our <span className="gt">Services</span></h2>
              <p className="sec-sub">End-to-end digital solutions for startups, SMEs, and enterprises across India.</p>
            </div>
            <Link to="/services" className="lm">All Services →</Link>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:22 }} className="svc-preview-grid">
            {SERVICES.slice(0,3).map((s,i) => (
              <Link to={`/services#${s.slug}`} key={s.slug} style={{ textDecoration:'none' }}>
                <article className="reveal" style={{ position:'relative', borderRadius:22, overflow:'hidden', minHeight:320, display:'flex', flexDirection:'column', justifyContent:'flex-end', transition:'all .3s', boxShadow:'var(--sh)', transitionDelay:`${i*.07}s` }}
                  onMouseOver={e=>{ e.currentTarget.style.transform='translateY(-8px)'; e.currentTarget.style.boxShadow='var(--sh-xl)'; }}
                  onMouseOut={e =>{  e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow='var(--sh)'; }}>
                  <div style={{ position:'absolute', inset:0, backgroundImage:`url('${s.img}')`, backgroundSize:'cover', backgroundPosition:'center' }}/>
                  <div style={{ position:'absolute', inset:0, background:'linear-gradient(to bottom,rgba(18,9,30,.18) 0%,rgba(18,9,30,.88) 100%)' }}/>
                  <div style={{ position:'absolute', top:16, left:16, width:42, height:42, borderRadius:12, background:'rgba(255,255,255,.15)', backdropFilter:'blur(10px)', border:'1px solid rgba(255,255,255,.3)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.25rem' }}>{s.icon}</div>
                  <div style={{ position:'absolute', top:16, right:16, background:'rgba(255,255,255,.15)', backdropFilter:'blur(10px)', border:'1px solid rgba(255,255,255,.3)', borderRadius:100, padding:'4px 12px', fontSize:'.66rem', fontWeight:800, color:'#fff', textTransform:'uppercase', letterSpacing:'.07em' }}>{s.badge}</div>
                  <div style={{ position:'relative', zIndex:2, padding:'20px 20px 22px' }}>
                    <h3 style={{ fontFamily:"'Outfit',sans-serif", fontSize:'1.05rem', fontWeight:800, color:'#fff', marginBottom:7 }}>{s.title}</h3>
                    <p style={{ fontSize:'.83rem', color:'rgba(255,255,255,.75)', lineHeight:1.65, marginBottom:14 }}>{s.desc}</p>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <span style={{ fontSize:'.78rem', fontWeight:800, color:'var(--orange)' }}>{s.price}</span>
                      <span style={{ fontSize:'.74rem', fontWeight:700, color:'rgba(255,255,255,.85)', background:'rgba(255,255,255,.12)', border:'1px solid rgba(255,255,255,.2)', padding:'4px 12px', borderRadius:100 }}>Get Quote →</span>
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>
          <div style={{ textAlign:'center', marginTop:40 }}>
            <Link to="/services" className="btn-main">See All Services →</Link>
          </div>
        </div>
      </section>

      {/* ══ OUR STORY ═════════════════════════════════════ */}
      <section style={{ background:'var(--bg3)', padding:'96px 0' }}>
        <div className="container">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:64, alignItems:'center' }} className="why-2col">
            <div className="rl">
              <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=700&q=80" alt="ITClave team" loading="lazy" style={{ width:'100%', borderRadius:24, boxShadow:'0 24px 80px rgba(123,82,171,.18)' }}/>
            </div>
            <div className="rr">
              <div className="eyebrow">Our Story</div>
              <h2 className="sec-title">Building Digital India, <span className="gt">One Business at a Time</span></h2>
              <p style={{ color:'var(--text2)', fontSize:'.95rem', lineHeight:1.82, marginBottom:14 }}>
                Founded in 2026, ITClave started with a simple mission: bring world-class software and digital marketing to Indian businesses at prices that make sense.
              </p>
              <p style={{ color:'var(--text2)', fontSize:'.95rem', lineHeight:1.82, marginBottom:28 }}>
                We've delivered 200+ projects — from startup MVPs to enterprise ERP systems — for clients across Delhi, Mumbai, Bangalore, Hyderabad, Pune and beyond. Always on time. Always transparent.
              </p>
              <Link to="/about" className="btn-main">Know More About Us →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ══ PORTFOLIO PREVIEW ═════════════════════════════ */}
      <section style={{ background:'#fff', padding:'96px 0' }}>
        <div className="container">
          <div className="sec-hdr">
            <div>
              <div className="eyebrow">Our Work</div>
              <h2 className="sec-title">Featured <span className="gt">Portfolio</span></h2>
              <p className="sec-sub">Real results for real businesses across India.</p>
            </div>
            <Link to="/portfolio" className="lm">All Projects →</Link>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:22 }} className="port-preview-grid">
            {PORTFOLIO.slice(0,3).map((p,i) => (
              <article key={i} className="reveal" style={{ borderRadius:22, overflow:'hidden', boxShadow:'var(--sh)', transition:'all .3s', transitionDelay:`${i*.07}s`, background:'#fff' }}
                onMouseOver={e=>{ e.currentTarget.style.transform='translateY(-8px)'; e.currentTarget.style.boxShadow='var(--sh-xl)'; }}
                onMouseOut={e =>{  e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow='var(--sh)'; }}>
                <div style={{ height:200, backgroundImage:`url('${p.img}')`, backgroundSize:'cover', backgroundPosition:'center', position:'relative' }}>
                  <div style={{ position:'absolute', inset:0, background:'linear-gradient(to bottom,transparent 40%,rgba(18,9,30,.75) 100%)' }}/>
                  <span style={{ position:'absolute', top:14, left:14, background:'rgba(255,255,255,.18)', backdropFilter:'blur(8px)', border:'1px solid rgba(255,255,255,.3)', borderRadius:100, padding:'4px 12px', fontSize:'.68rem', fontWeight:800, color:'#fff', textTransform:'uppercase', letterSpacing:'.07em' }}>{p.cl}</span>
                </div>
                <div style={{ padding:'20px 22px 22px' }}>
                  <h3 style={{ fontFamily:"'Outfit',sans-serif", fontSize:'1rem', fontWeight:800, color:'var(--text)', marginBottom:8 }}>{p.n}</h3>
                  <p style={{ fontSize:'.85rem', color:'var(--green,#22c55e)', fontWeight:700, marginBottom:12 }}>{p.r}</p>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                    {p.tech.map(t => <span key={t} style={{ background:'var(--purple-lt)', color:'var(--purple)', borderRadius:100, padding:'3px 10px', fontSize:'.72rem', fontWeight:700 }}>{t}</span>)}
                  </div>
                </div>
              </article>
            ))}
          </div>
          <div style={{ textAlign:'center', marginTop:40 }}>
            <Link to="/portfolio" className="btn-main">See All Projects →</Link>
          </div>
        </div>
      </section>

      {/* ══ PRODUCTS PREVIEW ══════════════════════════════ */}
      <section style={{ background:'var(--bg3)', padding:'96px 0' }}>
        <div className="container">
          <div className="sec-hdr">
            <div>
              <div className="eyebrow">Our Products</div>
              <h2 className="sec-title">Ready-Made <span className="gt">Solutions</span></h2>
              <p className="sec-sub">Battle-tested software products for Indian businesses — deploy in days, not months.</p>
            </div>
            <Link to="/products" className="lm">All Products →</Link>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:22 }} className="prod-preview-grid">
            {PRODUCTS.slice(0,3).map((pr,i) => (
              <article key={i} className="reveal" style={{ background:'#fff', border:'1.5px solid var(--border2)', borderRadius:22, padding:28, transition:'all .3s', transitionDelay:`${i*.07}s` }}
                onMouseOver={e=>{ e.currentTarget.style.borderColor='rgba(123,82,171,.25)'; e.currentTarget.style.transform='translateY(-6px)'; e.currentTarget.style.boxShadow='var(--sh-lg)'; }}
                onMouseOut={e =>{  e.currentTarget.style.borderColor='var(--border2)'; e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow='none'; }}>
                <div style={{ fontSize:'2.4rem', marginBottom:12 }}>{pr.icon}</div>
                <h3 style={{ fontFamily:"'Outfit',sans-serif", fontSize:'1.1rem', fontWeight:900, color:'var(--text)', marginBottom:6 }}>{pr.name}</h3>
                <p style={{ fontSize:'.85rem', color:'var(--text2)', lineHeight:1.68, marginBottom:14 }}>{pr.tagline}</p>
                <ul style={{ listStyle:'none', display:'flex', flexDirection:'column', gap:6, marginBottom:14 }}>
                  {pr.feats.slice(0,3).map(f => <li key={f} style={{ fontSize:'.8rem', color:'var(--text2)', display:'flex', alignItems:'center', gap:7 }}><span style={{ color:'var(--purple)', fontWeight:800 }}>✓</span> {f}</li>)}
                </ul>
                <Link to="/products" style={{ fontSize:'.82rem', fontWeight:700, color:'var(--purple)', textDecoration:'none' }}>Learn More →</Link>
              </article>
            ))}
          </div>
          <div style={{ textAlign:'center', marginTop:40 }}>
            <Link to="/products" className="btn-main">See All Products →</Link>
          </div>
        </div>
      </section>

      {/* ══ WHY ITCLAVE ═══════════════════════════════════ */}
      <section style={{ background:'#fff', padding:'96px 0' }}>
        <div className="container">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:64, alignItems:'center' }} className="why-2col">
            <div className="rl">
              <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=700&q=80" alt="ITClave team" loading="lazy" style={{ width:'100%', borderRadius:24, boxShadow:'0 24px 80px rgba(123,82,171,.18)' }}/>
            </div>
            <div className="rr">
              <div className="eyebrow">Why ITClave</div>
              <h2 className="sec-title">India's Most <span className="gt">Trusted</span> Tech Partner</h2>
              {[
                { ic:'🏆', t:'200+ Projects Delivered',   d:'Across India — from early-stage startups to established enterprises in every sector.' },
                { ic:'🔧', t:'Full-Stack MERN Expertise',  d:'Expert React, Node.js, MongoDB & Express developers building fast, scalable applications.' },
                { ic:'💰', t:'Transparent INR Pricing',    d:'No hidden costs. Digital marketing from ₹6,000/mo, websites from ₹15,000.' },
                { ic:'📊', t:'Data-Driven Results',        d:'Real KPIs tracked — rankings, traffic, leads, revenue. Monthly transparent reports.' },
                { ic:'🤝', t:'Dedicated Support',          d:'Assigned project manager, WhatsApp support, and 24/7 monitoring for critical systems.' },
              ].map((w,i) => (
                <div key={i} style={{ background:'var(--bg3)', border:'1px solid var(--border2)', borderRadius:14, padding:16, marginBottom:10, display:'flex', alignItems:'flex-start', gap:12, transition:'all .3s' }}
                  onMouseOver={e=>{ e.currentTarget.style.borderColor='rgba(123,82,171,.2)'; e.currentTarget.style.transform='translateX(5px)'; }}
                  onMouseOut={e =>{  e.currentTarget.style.borderColor='var(--border2)'; e.currentTarget.style.transform=''; }}>
                  <div style={{ width:38, height:38, borderRadius:10, background:'var(--grad)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1rem', flexShrink:0 }}>{w.ic}</div>
                  <div><div style={{ fontFamily:"'Outfit',sans-serif", fontWeight:800, fontSize:'.9rem', color:'var(--text)', marginBottom:2 }}>{w.t}</div><div style={{ fontSize:'.82rem', color:'var(--text2)', lineHeight:1.65 }}>{w.d}</div></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ TESTIMONIALS ══════════════════════════════════ */}
      <section style={{ background:'var(--bg3)', padding:'96px 0' }}>
        <div className="container">
          <div className="sec-hdr">
            <div>
              <div className="eyebrow">Client Reviews</div>
              <h2 className="sec-title">What Our <span className="gt">Clients</span> Say</h2>
            </div>
            <div style={{ textAlign:'right', flexShrink:0 }}>
              <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:'2.3rem', fontWeight:900, letterSpacing:'-.04em', background:'linear-gradient(90deg,#E8315B,#7B52AB,#4BB8E8)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text', lineHeight:1 }}>4.9★</div>
              <div style={{ fontSize:'.72rem', color:'var(--text3)', fontWeight:600, marginTop:3 }}>150+ Google Reviews</div>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:20 }} className="tst-preview-grid">
            {TESTIMONIALS.slice(0,3).map((t,i) => (
              <article key={i} className="reveal" style={{ background:'#fff', border:'1.5px solid var(--border2)', borderRadius:22, padding:28, transition:'all .3s', transitionDelay:`${i*.1}s`, position:'relative', overflow:'hidden' }}
                onMouseOver={e=>{ e.currentTarget.style.borderColor='rgba(123,82,171,.2)'; e.currentTarget.style.transform='translateY(-5px)'; e.currentTarget.style.boxShadow='var(--sh-lg)'; }}
                onMouseOut={e =>{  e.currentTarget.style.borderColor='var(--border2)'; e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow='none'; }}>
                <div style={{ position:'absolute', top:-10, right:20, fontSize:'7rem', fontWeight:900, color:'var(--purple)', opacity:.04, lineHeight:1 }}>"</div>
                <div style={{ display:'flex', gap:3, marginBottom:14 }}>{[1,2,3,4,5].map(j=><span key={j} style={{ color:'#f59e0b', fontSize:'.9rem' }}>★</span>)}</div>
                <p style={{ fontSize:'.87rem', color:'var(--text2)', lineHeight:1.78, marginBottom:18, fontStyle:'italic', position:'relative', zIndex:1 }}>"{t.r}"</p>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <div style={{ width:40, height:40, borderRadius:'50%', background:'linear-gradient(135deg,#E8315B,#7B52AB,#4BB8E8)', color:'#fff', fontWeight:900, fontSize:'.78rem', fontFamily:"'Outfit',sans-serif", display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{t.av}</div>
                  <div><div style={{ fontWeight:800, fontSize:'.88rem', color:'var(--text)' }}>{t.n}</div><div style={{ fontSize:'.72rem', color:'var(--text3)', fontWeight:600 }}>{t.co} · {t.loc}</div></div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ══ BLOG PREVIEW ══════════════════════════════════ */}
      <section style={{ background:'#fff', padding:'96px 0' }}>
        <div className="container">
          <div className="sec-hdr">
            <div>
              <div className="eyebrow">Latest Insights</div>
              <h2 className="sec-title">From Our <span className="gt">Blog</span></h2>
              <p className="sec-sub">Actionable guides, tutorials, and insights for Indian businesses.</p>
            </div>
            <Link to="/blog" className="lm">All Articles →</Link>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:22 }} className="blog-preview-grid">
            {BLOGS.slice(0,3).map((b,i) => (
              <article key={b.slug} className="reveal" style={{ borderRadius:22, overflow:'hidden', boxShadow:'var(--sh)', background:'#fff', transition:'all .3s', transitionDelay:`${i*.07}s` }}
                onMouseOver={e=>{ e.currentTarget.style.transform='translateY(-6px)'; e.currentTarget.style.boxShadow='var(--sh-xl)'; }}
                onMouseOut={e =>{  e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow='var(--sh)'; }}>
                <div style={{ height:190, backgroundImage:`url('${b.img}')`, backgroundSize:'cover', backgroundPosition:'center', position:'relative' }}>
                  <div style={{ position:'absolute', inset:0, background:'linear-gradient(to bottom,transparent 40%,rgba(18,9,30,.6) 100%)' }}/>
                  <span style={{ position:'absolute', top:14, left:14, background:'rgba(255,255,255,.18)', backdropFilter:'blur(8px)', border:'1px solid rgba(255,255,255,.3)', borderRadius:100, padding:'4px 12px', fontSize:'.66rem', fontWeight:800, color:'#fff', textTransform:'uppercase', letterSpacing:'.07em' }}>{b.cat}</span>
                </div>
                <div style={{ padding:'20px 22px 24px' }}>
                  <div style={{ display:'flex', gap:14, marginBottom:10, flexWrap:'wrap' }}>
                    <span style={{ fontSize:'.72rem', color:'var(--text3)', fontWeight:600 }}>📅 {b.date}</span>
                    <span style={{ fontSize:'.72rem', color:'var(--text3)', fontWeight:600 }}>⏱ {b.read} read</span>
                  </div>
                  <h3 style={{ fontFamily:"'Outfit',sans-serif", fontSize:'.98rem', fontWeight:800, color:'var(--text)', lineHeight:1.4, marginBottom:9 }}>{b.title}</h3>
                  <p style={{ fontSize:'.83rem', color:'var(--text2)', lineHeight:1.65, marginBottom:14 }}>{b.excerpt.slice(0,100)}…</p>
                  <Link to={`/blog#${b.slug}`} style={{ fontSize:'.8rem', fontWeight:700, color:'var(--purple)', textDecoration:'none' }}>Read More →</Link>
                </div>
              </article>
            ))}
          </div>
          <div style={{ textAlign:'center', marginTop:40 }}>
            <Link to="/blog" className="btn-main">See All Articles →</Link>
          </div>
        </div>
      </section>

      {/* ══ CTA PARALLAX BAND ═════════════════════════════ */}
      <section style={{ position:'relative', overflow:'hidden' }}>
        <ParallaxBg url="https://images.unsplash.com/photo-1677442135703-1787eea5ce01?w=1600&q=80" minHeight="420px">
          {/* Animated orbs */}
          {[
            { w:320, h:320, t:'10%',  l:'-5%',  a:'orbFloat1 12s ease-in-out infinite'     },
            { w:200, h:200, t:'55%',  l:'15%',  a:'orbFloat2 9s ease-in-out infinite 1s'   },
            { w:260, h:260, t:'5%',   r:'10%',  a:'orbFloat1 14s ease-in-out infinite 2s'  },
            { w:160, h:160, b:'5%',   r:'25%',  a:'orbFloat2 8s ease-in-out infinite .5s'  },
          ].map((o,i)=>(
            <div key={i} style={{ position:'absolute', width:o.w, height:o.h, borderRadius:'50%', background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.07)', top:o.t, left:o.l, right:o.r, bottom:o.b, animation:o.a, pointerEvents:'none' }}/>
          ))}
          <div style={{ position:'absolute', inset:0, backgroundImage:'radial-gradient(circle,rgba(255,255,255,.06) 1px,transparent 1px)', backgroundSize:'40px 40px', pointerEvents:'none' }}/>
          {/* Floating AI tags */}
          {[
            { label:'🤖 AI Powered', t:'15%', l:'8%'  },
            { label:'⚡ Fast Deploy',t:'72%', l:'6%'  },
            { label:'🔒 Secure APIs',t:'18%', r:'8%'  },
            { label:'📊 Analytics',  b:'18%', r:'9%'  },
          ].map((tag,i)=>(
            <div key={i} style={{ position:'absolute', top:tag.t, left:tag.l, right:tag.r, bottom:tag.b, background:'rgba(255,255,255,.10)', backdropFilter:'blur(8px)', border:'1px solid rgba(255,255,255,.2)', borderRadius:100, padding:'7px 16px', fontSize:'.74rem', fontWeight:700, color:'rgba(255,255,255,.92)', animation:`orbFloat${i%2===0?1:2} ${7+i}s ease-in-out infinite ${i*.8}s`, whiteSpace:'nowrap', pointerEvents:'none' }}>{tag.label}</div>
          ))}

          <div className="container" style={{ position:'relative', zIndex:2 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:40, flexWrap:'wrap', padding:'60px 0' }}>
              <div>
                <h2 style={{ fontFamily:"'Outfit',sans-serif", fontSize:'clamp(1.8rem,3.5vw,2.6rem)', fontWeight:900, color:'#fff', letterSpacing:'-.04em', lineHeight:1.1 }}>
                  Ready to Grow Your Business Across India?
                </h2>
                <p style={{ color:'rgba(255,255,255,.78)', marginTop:10, fontSize:'1rem' }}>Free consultation. Custom strategy. No obligation. Reply within 24 hours.</p>
                <div style={{ display:'flex', gap:14, flexWrap:'wrap', marginTop:18 }}>
                  {['🔥 200+ Projects','⭐ 4.9 Rated','🇮🇳 Pan-India','🚀 Agile Team'].map(t=>(
                    <span key={t} style={{ background:'rgba(255,255,255,.12)', border:'1px solid rgba(255,255,255,.2)', borderRadius:100, padding:'5px 14px', fontSize:'.76rem', fontWeight:700, color:'rgba(255,255,255,.9)' }}>{t}</span>
                  ))}
                </div>
              </div>
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:10, flexShrink:0 }}>
                <Link to="/contact" className="btn-white">🚀 Get Free Consultation</Link>
                <span style={{ fontSize:'.74rem', color:'rgba(255,255,255,.65)', fontWeight:600 }}>📞 +91 78087 07393 · Mon–Sat 9AM–6PM</span>
              </div>
            </div>
          </div>
        </ParallaxBg>
      </section>

      {/* ══ GLOBAL EXTRA STYLES ════════════════════════════ */}
      <style>{`
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes orbFloat1 { 0%,100%{transform:translateY(0) scale(1)} 50%{transform:translateY(-22px) scale(1.03)} }
        @keyframes orbFloat2 { 0%,100%{transform:translateY(0) scale(1)} 50%{transform:translateY(18px) scale(.97)} }
        .pulse-dot-light { display:inline-block; width:7px; height:7px; borderRadius:50%; background:rgba(255,255,255,.8); animation:pulseDotLight 1.8s ease-in-out infinite; }
        @keyframes pulseDotLight { 0%,100%{box-shadow:0 0 0 0 rgba(255,255,255,.6)} 60%{box-shadow:0 0 0 6px rgba(255,255,255,0)} }
        @media(max-width:1024px){
          .hero-2col{grid-template-columns:1fr!important}
          .hero-right-hide{display:none!important}
          .why-2col{grid-template-columns:1fr!important}
          .svc-preview-grid,.port-preview-grid,.prod-preview-grid,.blog-preview-grid,.tst-preview-grid{grid-template-columns:repeat(2,1fr)!important}
        }
        @media(max-width:640px){
          .svc-preview-grid,.port-preview-grid,.prod-preview-grid,.blog-preview-grid,.tst-preview-grid{grid-template-columns:1fr!important}
        }
      `}</style>
    </>
  );
}
